import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";

const BrandingContext = createContext(null);

const FALLBACK = {
  primary: "222 47% 15%",
  accent: "173 80% 28%",
  secondary: "210 40% 96.1%",
  primary_dark: "210 40% 98%",
  accent_dark: "173 65% 45%",
  theme_mode: "system",
};

function hslLuminance(hsl) {
  // hsl string "H S% L%" -> relative lightness heuristic via L
  const parts = String(hsl).split(" ");
  const l = parseFloat(parts[2]);
  return isNaN(l) ? 50 : l;
}

export function contrastWarning(hsl) {
  // crude check: very light primary on light bg or very dark accent fails visibility
  const l = hslLuminance(hsl);
  return l > 80 || l < 8;
}

export function BrandingProvider({ children }) {
  const { tenant } = useAuth();
  const [dark, setDark] = useState(false);
  const branding = tenant?.branding || FALLBACK;

  const computeDark = useCallback(() => {
    const override = localStorage.getItem("pnc_theme");
    const mode = override || branding.theme_mode || "system";
    if (mode === "dark") return true;
    if (mode === "light") return false;
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  }, [branding.theme_mode]);

  useEffect(() => { setDark(computeDark()); }, [computeDark]);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("dark", dark);
    const primary = dark ? (branding.primary_dark || FALLBACK.primary_dark) : (branding.primary || FALLBACK.primary);
    const accent = dark ? (branding.accent_dark || FALLBACK.accent_dark) : (branding.accent || FALLBACK.accent);
    root.style.setProperty("--primary", primary);
    root.style.setProperty("--accent", accent);
    root.style.setProperty("--ring", accent);
    if (!dark && branding.secondary) root.style.setProperty("--secondary", branding.secondary);
  }, [dark, branding]);

  const toggleTheme = useCallback(() => {
    const next = dark ? "light" : "dark";
    localStorage.setItem("pnc_theme", next);
    setDark(next === "dark");
  }, [dark]);

  const applyPreview = useCallback((preview, previewDark) => {
    const root = document.documentElement;
    const d = previewDark ?? dark;
    root.style.setProperty("--primary", d ? (preview.primary_dark || preview.primary) : preview.primary);
    root.style.setProperty("--accent", d ? (preview.accent_dark || preview.accent) : preview.accent);
    root.style.setProperty("--ring", d ? (preview.accent_dark || preview.accent) : preview.accent);
  }, [dark]);

  return (
    <BrandingContext.Provider value={{ branding, dark, toggleTheme, applyPreview, logo: branding.logo_data }}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext);
}
